import type { RouteLocationRaw } from 'vue-router';
import type { AppNotification } from '@/store/notificationStore';

/** Each stage of the course-registration approval chain notifies its approver. */
const COURSE_REGISTRATION_APPROVAL_TYPES = new Set([
  'COURSE_REGISTRATION_PENDING',
  'COURSE_REGISTRATION_AWAITING_HOD',
  'COURSE_REGISTRATION_AWAITING_SENATE',
]);

/**
 * Presentation rules shared by the bell overlay and the notification centre
 * (#224). Both render the same feed, so the icon and the click destination for
 * a given type must be decided in one place — otherwise a new notification type
 * routes correctly from the bell and dead-ends in the centre.
 */
export function notificationIconClass(type: string): string {
  if (COURSE_REGISTRATION_APPROVAL_TYPES.has(type)) return 'pi pi-check-square text-indigo-600 bg-indigo-100';
  switch (type) {
  case 'FORM_SUBMISSION_RECEIVED':
    return 'pi pi-graduation-cap text-primary bg-primary-100';
  case 'FORM_SUBMISSION_ASSIGNED':
    return 'pi pi-user-plus text-primary bg-primary-100';
  case 'ticket_message':
    return 'pi pi-envelope text-cyan-600 bg-cyan-100';
  case 'chat_message':
    return 'pi pi-comments text-primary bg-primary-100';
  case 'BILLING_ENROLLMENT':
    return 'pi pi-money-bill text-green-600 bg-green-100';
  case 'COURSE_REGISTRATION_PENDING':
    return 'pi pi-check-square text-indigo-600 bg-indigo-100';
  case 'ATTENDANCE_REMINDER':
    return 'pi pi-calendar-clock text-amber-600 bg-amber-100';
  default:
    return 'pi pi-info-circle text-gray-500 bg-gray-100';
  }
}

/**
 * Where clicking a notification should go, or null when the type has no
 * destination (the ticket badge opens the widget instead of navigating).
 * `currentPath` decides the shell: every target below is admin-only, so a
 * student row stays put rather than pushing a route they cannot open.
 */
export function notificationRouteFor(
  notif: Pick<AppNotification, 'type' | 'metadata'>,
  currentPath: string,
): RouteLocationRaw | null {
  if (currentPath.startsWith('/student')) return null;

  if (
    (notif.type === 'FORM_SUBMISSION_RECEIVED' || notif.type === 'FORM_SUBMISSION_ASSIGNED')
    && notif.metadata?.form_id
  ) {
    return { name: 'admin-forms-submissions', params: { id: String(notif.metadata.form_id) } };
  }

  if (COURSE_REGISTRATION_APPROVAL_TYPES.has(notif.type)) {
    // The approvals queue is a tab of the courses page (`registration-approvals`
    // is an unnamed redirect to it, so it can't be pushed by name).
    return { name: 'admin-course', query: { tab: 'approvals' } };
  }

  if (notif.type === 'chat_message') {
    return { name: 'admin-chat' };
  }

  // Dispatcher-sent types (#561) carry their destination as a path in
  // metadata.link, so a new type routes without a frontend release.
  if (typeof notif.metadata?.link === 'string' && notif.metadata.link.startsWith('/')) {
    return { path: notif.metadata.link };
  }

  return null;
}
